import { primaryColor } from '../../../utils/theme';
import TabPanel from '@mui/lab/TabPanel'
import {
  Box, 
  Grid, 
  Button,
  TextField,
  Tab,
  alpha,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead
} from "@mui/material";
import styled from "styled-components";

interface PageCardProps {
  padding:number;
  backgroundRequired: boolean
}

export const PageCard = styled(Box)<PageCardProps>(({ theme, padding, backgroundRequired=true }) => ({
  padding: theme.spacing(padding),
  backgroundColor: (backgroundRequired)? theme.palette.white.main : 'transparent',
  borderRadius: theme.palette.custom.borderRadius,
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
}));

export const TabTitle = styled(Box)(({ theme }) => ({
  color: theme.palette.white.main,
  fontWeight: 'bold',
  fontSize: '16px',
  backgroundColor: theme.palette.primary.main,
  padding: theme.spacing(1),
  textTransform: 'capitalize',
  
}))

export const BasicDetailItemContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(2),
  minHeight: theme.spacing(9)
}));

export const BasicDetailItemBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const CustomCard = styled(Box)(({theme}) => ({
  marginBottom: theme.spacing(4),
}));

export const AddButton = styled(Button)(({theme}) => ({
  marginTop: `${theme.spacing(-1)} !important`,
  padding: `${0} !important`
}));

export const CustomTextField = styled(TextField)(({theme}) => ({
  
}));

export const DocsContainer = styled(Box)(({theme}) => ({
  marginTop: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.palette.custom.borderRadius,
}));

export const DateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(3),
}));

export const DateTextField = styled(TextField)(({ theme }) => ({
  width: theme.spacing(25), // Increased width for better readability
  margin: theme.spacing(0, 1),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    '& input': {
      textAlign: 'center',
      padding: '10px 8px', // Adjust padding to match the design
      fontSize: '0.9rem',
    },
    '& fieldset': {
      borderColor: '#e0e0e0', // Light grey border
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

export const ButtonGroupContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2), 
  justifyContent: 'flex-end',
}));

const ActionButton = styled(Button)(({theme}) => ({
  padding: theme.spacing(1, 3),
  borderRadius: `${theme.spacing(1)} !important`,
  textTransform: 'capitalize',
  minWidth: '200px !important',
}))

export const CancelButton = styled(ActionButton)(({theme}) => ({
  borderColor: `${theme.palette.success.main} !important`,
  color: `${theme.palette.success.main} !important`,

  '&.Mui-disabled': {
    borderColor: `${theme.palette.grey[300]} !important`,
    color: `${theme.palette.grey[300]} !important`,
    opacity: 0.5
  }
}));

export const SaveButton = styled(ActionButton)(({theme}) => ({
  borderColor: theme.palette.success.main,
  color: theme.palette.white.main,

  '&.Mui-disabled': {
    borderColor: `${theme.palette.grey[300]} !important`,
    color: `${theme.palette.grey[300]} !important`,
    opacity: 0.5
  }
  
  // '&:hover': {
  //   backgroundColor: theme.palette.success.main,
  //   color: theme.palette.white.main,
  // }
}))

export const VerificationImageContainer = styled(Grid)(({theme}) => ({
  marginTop: theme.spacing(0.5),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& img': {
      // You'll need to define a max size for the icon so it doesn't break the layout
      width: '40px', 
      height: '40px',
  },
}))




export const CustomTab = styled(Tab)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: '600',
  textTransform: 'none',
  color: theme.palette.grey[600],
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

export const CustomTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: `0 !important`,
  paddingTop: `${theme.spacing(2, 2)} !important`,
}));


export const SummaryCardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
  gap: '20px',
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(2),
}));

export const SummaryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(4),
  display: 'flex',
  // Ensure content and icon are spaced out
  justifyContent: 'space-between', 
  alignItems: 'flex-start', // Align items to the top (important for right corner placement)
  gap: theme.spacing(2),
  height: '100%',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  // borderRadius: theme.shape.borderRadius,
}));

export const SummaryIconWrapper = styled(Box)<{ colorName: 'primary' | 'info' | 'error' | 'warning' | 'success' | 'secondary' }>(({ theme, colorName }) => {
  // Fixed the TypeScript structure issue from the previous turn
  const color = theme.palette[colorName]?.main || theme.palette.grey[300];
  
  return {
    backgroundColor: alpha(color, 0.1),
    borderRadius: '8px', 
    padding: theme.spacing(1.5), 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50px', 
    height: '50px',
    flexShrink: 0, 
    
    // Aligns the icon to the top right of the card by applying a negative margin
    marginTop: theme.spacing(-1), 
    marginRight: theme.spacing(-1),

    '& img': { 
      width: '30px', 
      height: '30px',
      color: color, 
    },
    '& svg': { 
        fontSize: '1.8rem', 
        color: color,
    },
  };
});

export const SummaryContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Allow content to grow and push the icon to the right
  flexGrow: 1, 
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem', 
  fontWeight: 500,
  color: theme.palette.grey[600],
  marginBottom: theme.spacing(0.5),
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem', 
  fontWeight: 700,
  color: theme.palette.custom.sidebarText, 
  lineHeight: 1.1,
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
  
  '& .unit-text': {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.grey[600],
  },
}));


// table styles

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light, 
  
  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText,
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main, // White rows
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.primary.light, // Very light grey stripe
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
}));

export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2), 
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));

interface StatusTagProps {
  status: 'Active' | 'Pending' | 'Blocked' | string;
}

export const StatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status) {
    case 'active':
      bgColor = alpha(primaryColor, 0.15);
      color = primaryColor;
      break;
    case 'pending':
      bgColor = alpha(theme.palette.warning.light, 0.5);
      color = theme.palette.warning.dark;
      break;
    case 'blocked':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.error.main;
      break;
    case 'invitation_sent':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.link.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    textAlign: 'center',
    width: 'fit-content',
    float: "left",
    margin: "0 auto",
  };
});